/**
 * Personalakte-Tab „Vorsorge" (G-24/G-25): Soll-Liste der Pflicht-
 * Untersuchungen aus den Stellenzuordnungen (requiredFor), Ist-Records des
 * Mitarbeiters und der „Fehlende anlegen"-Button (syncRequired, idempotent)
 * mit Ergebnis-Toast.
 */

import { useMemo } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { CheckCircle2, CircleDashed, ListPlus } from "lucide-react"
import { toast } from "sonner"
import { EXAMINATION_STATUS } from "@/components/examinations/labels"
import { DueBadge, LoadingSpinner } from "@/components/shared"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { DataGrid, type ColumnDef } from "@/components/ui/data-grid"
import { fmtDate } from "@/lib/format"
import { api, apiError } from "@/services/api"
import type { ExaminationRecord } from "@/types"

export function ExaminationsTab({
  employeeId,
  canWrite,
}: {
  employeeId: string
  canWrite: boolean
}) {
  const queryClient = useQueryClient()

  const required = useQuery({
    queryKey: ["required-examinations", employeeId],
    queryFn: () => api.examinations.requiredFor(employeeId),
  })

  const records = useQuery({
    queryKey: ["examination-records", "employee", employeeId],
    queryFn: () => api.examinations.records.list({ employee: Number(employeeId), size: 100 }),
  })

  const sync = useMutation({
    mutationFn: () => api.examinations.syncRequired(employeeId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["examination-records"] })
      queryClient.invalidateQueries({ queryKey: ["employee", employeeId] })
      toast.success(
        `Soll/Ist-Abgleich: ${result.created} angelegt, ${result.existing} bereits vorhanden`
      )
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const recordRows = records.data?.data ?? []
  const coveredExaminationIds = new Set(recordRows.map((r) => r.examinationId))
  const requiredRows = required.data ?? []
  const missing = requiredRows.filter((ex) => !coveredExaminationIds.has(ex.id))

  // Grid-Spalten: header als String, damit die Spalten-Auswahl ihn anzeigen kann
  const columns = useMemo<ColumnDef<ExaminationRecord>[]>(
    () => [
      {
        id: "examinationName",
        header: "Untersuchung",
        accessorFn: (r) => r.examinationName,
        meta: { cellClassName: "font-medium" },
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (r) => EXAMINATION_STATUS[r.status].label,
        cell: ({ row }) => (
          <Badge variant="outline" className={EXAMINATION_STATUS[row.original.status].badge}>
            {EXAMINATION_STATUS[row.original.status].label}
          </Badge>
        ),
      },
      {
        id: "examinedAt",
        header: "Untersucht am",
        // ISO-Datum sortiert chronologisch korrekt
        accessorFn: (r) => r.examinedAt ?? "",
        cell: ({ row }) => (row.original.examinedAt ? fmtDate(row.original.examinedAt) : "—"),
        meta: { cellClassName: "tabular-nums" },
      },
      {
        id: "dueDate",
        header: "Fällig am",
        accessorFn: (r) => r.dueDate ?? "",
        cell: ({ row }) => <DueBadge date={row.original.dueDate} />,
      },
    ],
    [],
  )

  return (
    <div className="space-y-3">
      {/* Soll-Liste (G-24) */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex flex-wrap items-center justify-between gap-2 text-base">
            <span>Pflicht-Untersuchungen laut Stellen (Soll)</span>
            {canWrite && (
              <Button
                size="sm"
                variant="outline"
                disabled={sync.isPending || missing.length === 0}
                onClick={() => sync.mutate()}
              >
                <ListPlus /> Fehlende anlegen ({missing.length})
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {required.isLoading ? (
            <LoadingSpinner className="h-6 w-6" />
          ) : requiredRows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Keine Pflicht-Untersuchungen für die zugeordneten Stellen.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {requiredRows.map((ex) => {
                const covered = coveredExaminationIds.has(ex.id)
                return (
                  <Badge
                    key={ex.id}
                    variant="outline"
                    className={
                      covered
                        ? "gap-1 border-green-300 bg-green-100 text-green-800"
                        : "gap-1 border-yellow-300 bg-yellow-100 text-yellow-800"
                    }
                    title={covered ? "Record vorhanden" : "Record fehlt"}
                  >
                    {covered ? (
                      <CheckCircle2 className="size-3" />
                    ) : (
                      <CircleDashed className="size-3" />
                    )}
                    {ex.name}
                  </Badge>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ist-Records */}
      <Card>
        <CardContent className="py-2">
          {records.isLoading ? (
            <LoadingSpinner fullPage />
          ) : (
            <DataGrid
              columns={columns}
              data={recordRows}
              storageKey="employee-examinations"
              emptyMessage="Keine Untersuchungen erfasst."
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}
